import { forwardRef, type ComponentProps } from 'react'

function classes(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(' ')
}

export type ButtonProps = ComponentProps<'button'> & {
  /** Visual treatment. @default 'solid' */
  variant?: 'solid' | 'outline' | 'ghost' | 'danger'
  /** Control density. @default 'medium' */
  size?: 'small' | 'medium' | 'icon'
  loading?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'solid', size = 'medium', loading = false, className, children, disabled, ...props }, ref,
) {
  return (
    <button
      ref={ref}
      data-slot="button"
      data-variant={variant}
      data-size={size}
      data-loading={loading}
      className={classes('button', className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <span className="spinner" aria-hidden="true" /> : null}
      {children}
    </button>
  )
})

export type BadgeProps = ComponentProps<'span'> & {
  tone?: 'neutral' | 'good' | 'warn' | 'bad' | 'accent'
  dot?: boolean
}

export function Badge({ tone = 'neutral', dot = false, className, children, ...props }: BadgeProps) {
  return (
    <span data-slot="badge" data-tone={tone} className={classes('badge', className)} {...props}>
      {dot ? <span className="badge__dot" aria-hidden="true" /> : null}{children}
    </span>
  )
}

export type PanelRootProps = ComponentProps<'section'>
export type PanelHeaderProps = ComponentProps<'header'>
export type PanelTitleProps = ComponentProps<'h2'>
export type PanelBodyProps = ComponentProps<'div'>

function PanelRoot({ className, ...props }: PanelRootProps) {
  return <section data-slot="panel" className={classes('panel', className)} {...props} />
}
function PanelHeader({ className, ...props }: PanelHeaderProps) {
  return <header data-slot="panel-header" className={classes('panel__header', className)} {...props} />
}
function PanelTitle({ className, ...props }: PanelTitleProps) {
  return <h2 data-slot="panel-title" className={classes('panel__title', className)} {...props} />
}
function PanelBody({ className, ...props }: PanelBodyProps) {
  return <div data-slot="panel-body" className={classes('panel__body', className)} {...props} />
}
export const Panel = { Root: PanelRoot, Header: PanelHeader, Title: PanelTitle, Body: PanelBody }

export type FieldProps = ComponentProps<'label'> & { label: string; hint?: string }
export function Field({ label, hint, className, children, ...props }: FieldProps) {
  return <label data-slot="field" className={classes('field', className)} {...props}><span className="field__label">{label}</span>{children}{hint ? <span className="field__hint">{hint}</span> : null}</label>
}

export type EmptyStateProps = ComponentProps<'div'> & { heading: string; description: string }
export function EmptyState({ heading, description, className, children, ...props }: EmptyStateProps) {
  return <div data-slot="empty-state" className={classes('empty-state', className)} {...props}><span className="empty-state__signal" aria-hidden="true" /><h2>{heading}</h2><p>{description}</p>{children}</div>
}

export function Skeleton({ className, ...props }: ComponentProps<'div'>) {
  return <div data-slot="skeleton" className={classes('skeleton', className)} aria-hidden="true" {...props} />
}

